import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useCallback } from "react";
import type { ReactElement } from "react";
import Sidebar from "./sidebar/Sidebar";
import { OrgUrlSyncProvider } from "./OrgUrlSyncProvider";
import AppSwitcherRail from "./sidebar/AppSwitcherRail";
import { useActiveApp, isCodesmithPath, isSettingsPath, isFixedMainPath, isStandaloneAccountPath, isAdminPath } from "./sidebar/useActiveApp";
import SettingsSidebar from "./sidebar/SettingsSidebar";
import AdminSidebar from "./sidebar/AdminSidebar";
import { useAuth } from "./AuthProvider";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { MigrationWizardDialog } from "./LazyMigrationWizard";
import { useLocalStorage } from "usehooks-ts";
import { SidebarProvider } from "@repo/blacksmith-ui/sidebar";
import { SidebarMainContent } from "./SidebarMainContent";
import { MainPaneLoader } from "./MainPaneLoader";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { ShortcutsModal } from "./ShortcutsModal";
import LiveMetricsSheetLoader from "./sidebar/LiveMetricsSheetLoader";
import { useShortcuts } from "@/hooks/useShortcuts";
import { CommandPalette } from "./CommandPalette";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { usePrefetchAssistantSessions } from "@/hooks/usePrefetchAssistantSessions";
import { usePrefetchCodesmithChunk } from "@/hooks/usePrefetchCodesmithChunk";
import { getSidebarStateFromStorage, RailSkeleton, SidebarLayoutSkeleton } from "./SidebarLayoutSkeleton";
export function SidebarLayout(t0) {
  const $ = _c(61);
  const {
    children,
    scrollableMain: t1
  } = t0;
  const scrollableMain = t1 === undefined ? true : t1;
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("sidebar-open", true);
  const [sidebarPreference, setSidebarPreference] = useLocalStorage("sidebar-preference", "auto");
  const {
    shortcutsModalOpen,
    setShortcutsModalOpen
  } = useShortcuts();
  const router = useRouter();
  let t2;
  if ($[0] !== router.pathname) {
    t2 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isCodesmithRoute = t2;
  let t3;
  if ($[2] !== router.pathname || $[3] !== scrollableMain) {
    t3 = scrollableMain === false || isFixedMainPath(router.pathname);
    $[2] = router.pathname;
    $[3] = scrollableMain;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const fixedMain = t3;
  let t4;
  if ($[5] !== router.asPath || $[6] !== router.query.embed) {
    t4 = router.query.embed === "1" || /(?:^|[?&])embed=1(?:&|$)/.test(router.asPath.split("#")[0] ?? "");
    $[5] = router.asPath;
    $[6] = router.query.embed;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isEmbed = t4;
  const activeApp = useActiveApp();
  let t5;
  if ($[8] !== router.pathname) {
    t5 = isStandaloneAccountPath(router.pathname);
    $[8] = router.pathname;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isStandalone = t5;
  const showContextualSidebar = !isEmbed && activeApp === "actions" && !isStandalone;
  let t6;
  if ($[10] !== router.pathname) {
    t6 = isSettingsPath(router.pathname);
    $[10] = router.pathname;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isSettingsRoute = t6;
  const showSettingsSidebar = !isEmbed && isSettingsRoute;
  const {
    user
  } = useAuth();
  let t7;
  if ($[12] !== isEmbed || $[13] !== router.pathname || $[14] !== user?.is_superuser) {
    t7 = !isEmbed && isAdminPath(router.pathname) && !!user?.is_superuser;
    $[12] = isEmbed;
    $[13] = router.pathname;
    $[14] = user?.is_superuser;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const showAdminSidebar = t7;
  useWorkflowCache();
  usePrefetchAssistantSessions();
  usePrefetchCodesmithChunk();
  let t8;
  if ($[16] !== setSidebarOpen || $[17] !== setSidebarPreference) {
    t8 = open => {
      setSidebarOpen(open);
      setSidebarPreference("manual");
    };
    $[16] = setSidebarOpen;
    $[17] = setSidebarPreference;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleSidebarChange = t8;
  let t10;
  let t9;
  if ($[19] !== setSidebarOpen || $[20] !== sidebarPreference) {
    t9 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQueryMD = window.matchMedia("(min-width: 768px)");
      const mediaQueryXL = window.matchMedia("(min-width: 1280px)");
      const updateSidebarState = () => {
        const isMD = mediaQueryMD.matches;
        const isXL = mediaQueryXL.matches;
        const shouldOpen = isMD && isXL;
        setSidebarOpen(currentOpen => {
          if (currentOpen !== shouldOpen) {
            return shouldOpen;
          }
          return currentOpen;
        });
      };
      if (sidebarPreference === "auto") {
        updateSidebarState();
      }
      const handleChange = () => {
        if (sidebarPreference === "manual") {
          return;
        }
        updateSidebarState();
      };
      if (mediaQueryMD.addEventListener && mediaQueryXL.addEventListener) {
        mediaQueryMD.addEventListener("change", handleChange);
        mediaQueryXL.addEventListener("change", handleChange);
        return () => {
          mediaQueryMD.removeEventListener("change", handleChange);
          mediaQueryXL.removeEventListener("change", handleChange);
        };
      } else {
        mediaQueryMD.addListener(handleChange);
        mediaQueryXL.addListener(handleChange);
        return () => {
          mediaQueryMD.removeListener(handleChange);
          mediaQueryXL.removeListener(handleChange);
        };
      }
    };
    t10 = [sidebarPreference, setSidebarOpen];
    $[19] = setSidebarOpen;
    $[20] = sidebarPreference;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarLayoutSkeleton />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = showContextualSidebar || showSettingsSidebar || showAdminSidebar;
  let t13;
  if ($[24] !== isEmbed) {
    t13 = !isEmbed && <Suspense fallback={<RailSkeleton />}><AppSwitcherRail /></Suspense>;
    $[24] = isEmbed;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== showContextualSidebar) {
    t14 = showContextualSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} />}><Sidebar /></Suspense>;
    $[26] = showContextualSidebar;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== showSettingsSidebar) {
    t15 = showSettingsSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><SettingsSidebar /></Suspense>;
    $[28] = showSettingsSidebar;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== showAdminSidebar) {
    t16 = showAdminSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><AdminSidebar /></Suspense>;
    $[30] = showAdminSidebar;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = isCodesmithRoute ? "codesmith" : isSettingsRoute ? "settings" : router.pathname;
  let t18;
  if ($[32] !== isCodesmithRoute) {
    t18 = isCodesmithRoute ? <SessionSkeleton /> : <MainPaneLoader />;
    $[32] = isCodesmithRoute;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== children || $[35] !== fixedMain || $[36] !== t17 || $[37] !== t18) {
    t19 = <SidebarMainContent scrollResetKey={t17} fixedMain={fixedMain} fallback={t18}>{children}</SidebarMainContent>;
    $[34] = children;
    $[35] = fixedMain;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t19) {
    t20 = <div className="flex-1 flex overflow-hidden">{t13}{t14}{t15}{t16}{t19}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== handleSidebarChange || $[46] !== sidebarOpen || $[47] !== t12 || $[48] !== t20) {
    t21 = <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange} enableKeyboardShortcut={t12} className="flex-1 min-h-0 relative">{t20}</SidebarProvider>;
    $[45] = handleSidebarChange;
    $[46] = sidebarOpen;
    $[47] = t12;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== setShortcutsModalOpen || $[51] !== shortcutsModalOpen) {
    t22 = <ShortcutsModal open={shortcutsModalOpen} onOpenChange={setShortcutsModalOpen} />;
    $[50] = setShortcutsModalOpen;
    $[51] = shortcutsModalOpen;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <CommandPalette />;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== isEmbed) {
    t24 = !isEmbed && <MigrationWizardDialog />;
    $[54] = isEmbed;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <LiveMetricsSheetLoader />;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t21 || $[58] !== t22 || $[59] !== t24) {
    t26 = <Suspense fallback={t11}><div className="flex flex-col h-dvh">{t21}{t22}{t23}{t24}{t25}</div></Suspense>;
    $[57] = t21;
    $[58] = t22;
    $[59] = t24;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
}

// Every page shares these helpers so the OrgUrlSyncProvider > SidebarLayout
// element shape is identical across routes and React reconciles the persistent
// chrome instead of remounting it. useOrgUrlSync is a no-op on routes without
// a URL org and skips /admin/internal-monitors, so mounting it globally is safe.
export function getSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}

// For pages whose content owns its scroll (scrollableMain={false}).
export function getFixedSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout scrollableMain={false}>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}